import { useState, type FormEvent } from 'react';
import { Download, Printer, Receipt, Search, User } from 'lucide-react';

import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { DatePicker } from '@/components/ui/DatePicker';
import { EmptyState } from '@/components/ui/EmptyState';
import { Input } from '@/components/ui/Input';
import { Pagination } from '@/components/ui/Pagination';
import { QueryError } from '@/components/ui/QueryError';
import { Select } from '@/components/ui/Select';
import { Table, type Column } from '@/components/ui/Table';
import { useApiQuery } from '@/hooks/useApiQuery';
import { toast } from '@/hooks/useToast';
import { useUrlFilters } from '@/hooks/useUrlFilters';
import { formatCurrency, formatDate } from '@/lib/format';
import { cn, getNameInitials } from '@/lib/utils';
import type { FeeDayBookEntry, FeePaymentMethod, FeeReceipt } from '@/types/fees';
import { feesApi } from '../api/fees.api';
import {
  FEE_METHOD_FILTER_OPTIONS,
  FEE_METHOD_LABEL,
  FEE_RECEIPTS_PER_PAGE,
} from '../constants';
import { CancelReceiptDialog } from './CancelReceiptDialog';
import { FeeReceiptPrint, type PrintableReceipt } from './FeeReceiptDocument';
import { Figure } from './LedgerFigures';

/**
 * The day book's filters, as they sit in the address bar under `collections.`.
 * The dates have no list: any day is a real choice, and the server validates
 * the format.
 */
type Filters = {
  page: number;
  from: string;
  to: string;
  method: FeePaymentMethod | '';
  search: string;
};

const DEFAULT_FILTERS: Filters = {
  page: 1,
  from: '',
  to: '',
  method: '',
  search: '',
};

const FILTER_OPTIONS = {
  prefix: 'collections',
  allowed: {
    method: Object.keys(FEE_METHOD_LABEL),
  },
};

export interface FeeCollectionsPanelProps {
  isCollege: boolean;
  canCollect: boolean;
  /** Opens the student's own account from a receipt row. */
  onOpenAccount: (studentUuid: string) => void;
  reloadToken: number;
  /** A cancellation here changes balances the register is showing. */
  onChanged: () => void;
}

/**
 * The day book: every receipt taken this session.
 *
 * What a counter is balanced against at the end of a day, and what an auditor
 * reads a year later — so cancelled receipts are listed too, struck through and
 * labelled. A reversal that was hidden is one nobody would notice.
 *
 * The date range is empty to begin with, which means the whole session. An
 * office narrows it to today when it cashes up; opening on today instead would
 * show an empty screen every morning before the first payment.
 */
export function FeeCollectionsPanel({
  isCollege,
  canCollect,
  onOpenAccount,
  reloadToken,
  onChanged,
}: FeeCollectionsPanelProps) {
  const [filters, setFilters] = useUrlFilters(DEFAULT_FILTERS, FILTER_OPTIONS);
  // Seeded from the address, so a restored search is shown in the box too.
  const [searchDraft, setSearchDraft] = useState(filters.search);
  const [cancelling, setCancelling] = useState<FeeReceipt | null>(null);
  /** The receipt on its way to the printer, or null. */
  const [printing, setPrinting] = useState<PrintableReceipt | null>(null);

  const { data, isLoading, error, refetch } = useApiQuery(
    () => feesApi.listPayments({ ...filters, limit: FEE_RECEIPTS_PER_PAGE }),
    [filters.page, filters.from, filters.to, filters.method, filters.search, reloadToken],
  );

  /** The receipt, plus who it was issued to — everything the paper names. */
  function printableFor(entry: FeeDayBookEntry): PrintableReceipt | null {
    if (!data) return null;

    return {
      receipt: entry,
      student: {
        fullName: entry.studentName,
        admissionNumber: entry.admissionNumber,
        // A college places nobody in a class, and the day book carries an em
        // dash there rather than a name. Nothing goes on the paper for it.
        className: isCollege ? null : entry.className,
        sectionName: isCollege ? null : entry.sectionName,
        // The day book does not carry roll numbers; the receipt names the
        // student by the admission number, which is the one that never changes.
        rollNumber: null,
      },
      sessionName: data.academicYear.name,
    };
  }

  function updateFilters(patch: Partial<Filters>) {
    setFilters({ ...patch, ...(patch.page ? {} : { page: 1 }) });
  }

  function submitSearch(event: FormEvent) {
    event.preventDefault();
    updateFilters({ search: searchDraft.trim() });
  }

  const hasFilters = Boolean(filters.from || filters.to || filters.method || filters.search);

  const columns: Column<FeeDayBookEntry>[] = [
    {
      key: 'receipt',
      header: 'Receipt',
      render: (entry) => (
        <div className="min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="font-mono text-base font-bold tabular-nums text-slate-800 dark:text-slate-100">
              #{entry.receiptNumber}
            </span>
            {entry.isCancelled && <Badge tone="critical">Cancelled</Badge>}
          </div>
          <p className="text-sm font-medium text-slate-500 dark:text-slate-400 mt-0.5">
            {formatDate(entry.paidOn)}
          </p>
        </div>
      ),
    },
    {
      key: 'student',
      header: 'Student',
      render: (entry) => (
        <div className="flex items-center gap-2.5 min-w-0">
          <span
            className="grid h-8 w-8 shrink-0 place-items-center rounded-xl bg-blue-50 dark:bg-blue-950/80 text-xs font-black text-blue-600 dark:text-blue-400 shadow-2xs border border-blue-200/50 dark:border-blue-800/40"
            aria-hidden
          >
            {getNameInitials(entry.studentName)}
          </span>
          <div className="min-w-0">
            <p className="truncate text-base font-bold text-slate-900 dark:text-white">
              {entry.studentName}
            </p>
            <p className="font-mono text-sm tabular-nums text-slate-500 dark:text-slate-400 truncate mt-0.5">
              {isCollege
                ? entry.admissionNumber
                : `${entry.admissionNumber} · ${entry.className ?? ''} ${entry.sectionName ?? ''}`}
            </p>
          </div>
        </div>
      ),
    },
    {
      key: 'method',
      header: 'Paid by',
      hideOnMobile: true,
      render: (entry) => (
        <div className="min-w-0">
          <span className="inline-flex items-center px-2 py-0.5 rounded-lg bg-slate-100 dark:bg-slate-800 text-xs font-bold text-slate-700 dark:text-slate-300">
            {FEE_METHOD_LABEL[entry.method]}
          </span>
          {entry.reference && (
            <p className="truncate font-mono text-sm text-slate-400 dark:text-slate-500 mt-0.5">
              Ref: {entry.reference}
            </p>
          )}
        </div>
      ),
    },
    {
      key: 'collectedBy',
      header: 'Taken by',
      hideOnMobile: true,
      render: (entry) => (
        <div className="flex items-center gap-1.5 min-w-0">
          <User className="h-3.5 w-3.5 text-slate-400 shrink-0" />
          <p className="truncate text-sm font-semibold text-slate-700 dark:text-slate-300">
            {entry.collectedBy}
          </p>
        </div>
      ),
    },
    {
      key: 'amount',
      header: 'Amount',
      align: 'right',
      render: (entry) => (
        <p
          className={cn(
            'text-sm font-black tabular-nums tracking-tight',
            entry.isCancelled
              ? 'text-slate-400 line-through'
              : 'text-slate-600 dark:text-white',
          )}
        >
          {formatCurrency(entry.amount)}
        </p>
      ),
    },
    {
      key: 'actions',
      header: <span className="sr-only">Actions</span>,
      align: 'right',
      render: (entry) => (
        <div className="flex items-center justify-end gap-1" onClick={(event) => event.stopPropagation()}>
          <Button
            variant="ghost"
            size="sm"
            className="px-2 hover:bg-slate-100 dark:hover:bg-slate-800"
            aria-label={`Print receipt ${entry.receiptNumber}`}
            title="Print this receipt"
            onClick={(event) => {
              event.stopPropagation();
              setPrinting(printableFor(entry));
            }}
          >
            <Printer className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="px-2 hover:bg-slate-100 dark:hover:bg-slate-800"
            aria-label={`Download receipt ${entry.receiptNumber}`}
            title="Choose “Save as PDF” in the print dialog"
            onClick={(event) => {
              event.stopPropagation();
              setPrinting(printableFor(entry));
            }}
          >
            <Download className="h-4 w-4" />
          </Button>
          {canCollect && !entry.isCancelled && (
            <Button
              variant="dangerSoft"
              size="sm"
              className="text-xs font-bold"
              aria-label={`Cancel receipt ${entry.receiptNumber}`}
              onClick={(event) => {
                event.stopPropagation();
                setCancelling(entry);
              }}
            >
              Cancel
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      {error && (
        <QueryError title="Could not load the collections" error={error} onRetry={refetch} />
      )}

      <div className="rounded-2xl sm:rounded-3xl bg-white dark:bg-[#111c2e] border border-slate-200/90 dark:border-slate-800 shadow-xs p-4 sm:p-5 flex flex-col gap-4 w-full max-w-full overflow-hidden">
        {/* Filtration Box at Top - All in a Single Line */}
        <div className="rounded-2xl p-3 bg-slate-50/80 dark:bg-[#0b1322] border border-slate-200/90 dark:border-slate-800 shadow-xs">
          <form onSubmit={submitSearch} className="flex flex-wrap lg:flex-nowrap items-end gap-2.5 w-full">
            <div className="flex-1 min-w-[200px]">
              <Input
                label="Search receipts"
                labelClassName="text-xs font-bold text-slate-700 dark:text-slate-200"
                placeholder="Receipt number, student name or admission number"
                type="search"
                autoComplete="off"
                className="!rounded-xl !h-9 !py-1 !pl-9 !pr-3 !text-[13px] !font-medium !text-slate-700 dark:!text-slate-200 !bg-white dark:!bg-[#111c2e] !border-slate-200 dark:!border-slate-700/80 shadow-2xs hover:!border-indigo-300 focus:!border-indigo-500 transition-colors w-full"
                leftIcon={<Search className="h-3.5 w-3.5 text-slate-400" />}
                value={searchDraft}
                onChange={(event) => setSearchDraft(event.target.value)}
              />
            </div>
            <Button
              type="submit"
              variant="secondary"
              className="!h-9 !py-1 !px-3.5 !text-[13px] font-bold border border-slate-200 dark:border-slate-700/80 bg-white dark:bg-[#111c2e] text-slate-700 dark:text-slate-200 hover:bg-slate-50 hover:text-indigo-600 hover:border-indigo-300 shadow-2xs rounded-xl flex items-center gap-1.5 shrink-0 transition-colors"
            >
              Search
            </Button>
            <div className="w-full sm:w-36 lg:w-36 shrink-0">
              <DatePicker
                label="From"
                labelClassName="text-xs font-bold text-slate-700 dark:text-slate-200"
                className="!rounded-xl !h-9 !py-1 !px-3 !text-[13px] !font-medium !text-slate-700 dark:!text-slate-200 !bg-white dark:!bg-[#111c2e] !border-slate-200 dark:!border-slate-700/80 shadow-2xs hover:!border-indigo-300 transition-colors w-full"
                isClearable
                value={filters.from ?? ''}
                max={filters.to || undefined}
                onChange={(event) => updateFilters({ from: event.target.value })}
              />
            </div>
            <div className="w-full sm:w-36 lg:w-36 shrink-0">
              <DatePicker
                label="To"
                labelClassName="text-xs font-bold text-slate-700 dark:text-slate-200"
                className="!rounded-xl !h-9 !py-1 !px-3 !text-[13px] !font-medium !text-slate-700 dark:!text-slate-200 !bg-white dark:!bg-[#111c2e] !border-slate-200 dark:!border-slate-700/80 shadow-2xs hover:!border-indigo-300 transition-colors w-full"
                isClearable
                value={filters.to ?? ''}
                min={filters.from || undefined}
                onChange={(event) => updateFilters({ to: event.target.value })}
              />
            </div>
            <div className="w-full sm:w-40 lg:w-40 shrink-0">
              <Select
                label="Paid by"
                labelClassName="text-xs font-bold text-slate-700 dark:text-slate-200"
                className="!rounded-xl !h-9 !py-1 !px-3 !text-[13px] !font-medium !text-slate-700 dark:!text-slate-200 !bg-white dark:!bg-[#111c2e] !border-slate-200 dark:!border-slate-700/80 shadow-2xs hover:!border-indigo-300 transition-colors w-full"
                options={FEE_METHOD_FILTER_OPTIONS}
                value={filters.method ?? ''}
                onChange={(event) =>
                  updateFilters({ method: event.target.value as Filters['method'] })
                }
              />
            </div>
            {Boolean(searchDraft || filters.from || filters.to || filters.method) && (
              <Button
                type="button"
                variant="secondary"
                className="!h-9 !py-1 !px-3 !text-[13px] font-bold border border-slate-200 dark:border-slate-700/80 bg-white dark:bg-[#111c2e] text-slate-700 dark:text-slate-200 hover:bg-slate-50 hover:text-rose-600 hover:border-rose-300 shadow-2xs rounded-xl flex items-center gap-1.5 shrink-0 transition-colors"
                onClick={() => {
                  setSearchDraft('');
                  updateFilters({ from: '', to: '', method: '', search: '' });
                }}
              >
                Clear
              </Button>
            )}
          </form>
        </div>

        {/* 2 Summary Cards (Collected & Cancelled) Below Filtration */}
        {data && (
          <div className="rounded-2xl border border-slate-200/80 dark:border-slate-800 bg-slate-50/80 dark:bg-slate-900/50 p-4">
            <dl className="grid grid-cols-2 gap-x-4 gap-y-3 sm:grid-cols-4">
              <Figure
                label="Collected"
                value={formatCurrency(data.totals.collected)}
                tone="blue"
                hint={`${data.meta.total.toLocaleString('en-IN')} receipt${data.meta.total === 1 ? '' : 's'}`}
              />
              <Figure
                label="Cancelled"
                value={
                  Number(data.totals.cancelled) > 0
                    ? formatCurrency(data.totals.cancelled)
                    : '—'
                }
                tone={Number(data.totals.cancelled) > 0 ? 'crit' : 'default'}
                hint="Taken back off accounts"
              />
            </dl>

            {/* What the drawer is counted against: so much cash, so much UPI. */}
            {data.byMethod.length > 0 && (
              <ul className="mt-3 flex flex-wrap gap-2">
                {data.byMethod.map((entry) => (
                  <li
                    key={entry.method}
                    className="inline-flex items-center gap-1.5 px-3 py-1 rounded-xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 shadow-2xs text-xs font-medium"
                    title={`${entry.count} receipt${entry.count === 1 ? '' : 's'}`}
                  >
                    <span>{FEE_METHOD_LABEL[entry.method]}:</span>{' '}
                    <span className="font-mono text-sm font-bold tabular-nums text-blue-700 dark:text-blue-300">
                      {formatCurrency(entry.amount)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* Table */}
        <div className="overflow-hidden rounded-2xl border border-slate-200/80 dark:border-slate-800">
          <Table
            columns={columns}
            rows={data?.items ?? []}
            rowKey={(entry) => entry.uuid}
            isLoading={isLoading}
            onRowClick={(entry) => onOpenAccount(entry.studentUuid)}
            empty={
              <EmptyState
                icon={<Receipt className="h-5 w-5" />}
                title={hasFilters ? 'No receipts match these filters' : 'Nothing collected yet'}
                description={
                  hasFilters
                    ? 'Widen the dates, or clear the filters to see the whole session.'
                    : 'Every payment taken at the counter appears here with its receipt number.'
                }
                action={
                  hasFilters ? (
                    <Button
                      variant="secondary"
                      onClick={() => {
                        setSearchDraft('');
                        updateFilters({ from: '', to: '', method: '', search: '' });
                      }}
                    >
                      Clear filters
                    </Button>
                  ) : undefined
                }
              />
            }
          />
        </div>

        {data && data.meta.total > 0 && (
          <Pagination hideBorder meta={data.meta} onPageChange={(page) => updateFilters({ page })} />
        )}
      </div>

      <CancelReceiptDialog
        receipt={cancelling}
        onCancelled={(receipt) => {
          setCancelling(null);
          refetch();
          onChanged();
          // Nothing else is over the page here, so the toast is visible — and
          // the row it describes is about to redraw as struck through.
          toast.success(
            `Receipt ${receipt.receiptNumber} is cancelled`,
            'The amount has been taken back off the account. The receipt stays on the ledger.',
          );
        }}
        onDismiss={() => setCancelling(null)}
      />

      <FeeReceiptPrint target={printing} onFinished={() => setPrinting(null)} />
    </div>
  );
}
